import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bot, MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import {
  getMenuCardStyle,
  getMenuMutedTextStyle,
  getMenuPrimaryButtonStyle,
  isPremiumMenu,
} from '@/lib/menuTheme';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { canShowPublicAssistant } from '@/lib/menuPlanCapabilities';

const FLOAT_DISMISS_KEY = (bid) => `soluqr_assistant_entry_float_dismissed_${bid}`;

/**
 * CTA hacia el asistente (catálogo / welcome). Sin lógica de chat.
 *
 * @param {object} props
 * @param {object|null} props.business — menú `business` (tema / nombre asistente)
 * @param {string} props.businessId — id efectivo para analytics
 * @param {string} props.routeBid — id o slug de ruta para `getPublicMenuPath`
 * @param {'floating'|'inline'} props.variant
 * @param {'catalog_floating'|'catalog_inline'|'welcome'} props.source
 * @param {boolean} [props.hasCart] — si hay carrito flotante (solo floating)
 */
export default function AssistantEntryCta({
  business,
  businessId,
  routeBid,
  variant = 'inline',
  source = 'catalog_inline',
  hasCart = false,
}) {
  const { t } = useTranslation();
  const bid = String(routeBid || businessId || '').trim();
  const analyticsBid = String(businessId || bid || '').trim();
  const to = bid ? getPublicMenuPath(bid, 'assistant') : '#';
  const premium = business && isPremiumMenu(business);

  const [floatDismissed, setFloatDismissed] = useState(false);

  useEffect(() => {
    if (variant !== 'floating' || !analyticsBid) return;
    try {
      setFloatDismissed(sessionStorage.getItem(FLOAT_DISMISS_KEY(analyticsBid)) === '1');
    } catch {
      setFloatDismissed(false);
    }
  }, [variant, analyticsBid]);

  const onNavigate = useCallback(() => {
    if (!analyticsBid) return;
    trackEvent('assistant_entry_clicked', {
      business_id: analyticsBid,
      source,
    });
  }, [analyticsBid, source]);

  const dismissFloating = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        sessionStorage.setItem(FLOAT_DISMISS_KEY(analyticsBid), '1');
      } catch {
        /* ignore */
      }
      setFloatDismissed(true);
    },
    [analyticsBid]
  );

  if (!bid || !analyticsBid) return null;
  if (business && !canShowPublicAssistant(business)) return null;

  if (variant === 'floating' && floatDismissed) return null;

  const Icon = Bot;
  const cardBase =
    'relative overflow-hidden rounded-2xl border transition-shadow';
  const cardPremium = premium
    ? {
        ...getMenuCardStyle(business),
        boxShadow: '0 0 28px -8px rgba(99, 102, 241, 0.12)',
      }
    : undefined;
  const muted = premium ? getMenuMutedTextStyle(business) : undefined;
  const btnPrimary = premium ? getMenuPrimaryButtonStyle(business) : undefined;

  const inner = (
    <>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border',
            premium ? 'border-white/15 bg-black/20' : 'border-border bg-muted/50'
          )}
        >
          <Icon className={cn('h-5 w-5', premium ? 'text-violet-200' : 'text-primary')} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1 pr-6">
          <div className="flex flex-wrap items-center gap-2">
            <p
              className={cn('text-sm font-semibold leading-snug', !premium && 'text-foreground')}
              style={premium ? { color: business?.text_color } : undefined}
            >
              {t('catalog.assistantTitle')}
            </p>
            <Badge
              variant="outline"
              className={cn(
                'h-5 border-emerald-500/35 bg-emerald-500/10 px-1.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300'
              )}
            >
              Online
            </Badge>
          </div>
          <p className={cn('text-xs leading-relaxed', !premium && 'text-muted-foreground')} style={muted}>
            {t('catalog.assistantBody')}
          </p>
        </div>
      </div>
      <Button
        asChild
        size="sm"
        className={cn('mt-3 w-full shadow-sm', !premium && 'shadow-md')}
        style={btnPrimary}
      >
        <Link to={to} onClick={onNavigate} className="gap-2">
          <MessageCircle className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          {t('catalog.assistantCta')}
        </Link>
      </Button>
    </>
  );

  if (variant === 'floating') {
    return (
      <div
        className={cn(
          'fixed z-[45] max-w-[min(100%,19rem)] sm:max-w-xs',
          hasCart ? 'bottom-24' : 'bottom-6',
          'right-4 sm:right-6'
        )}
      >
        <div className={cn(cardBase, 'p-3.5 shadow-lg', !premium && 'border-border/80 bg-card/95 backdrop-blur-md')} style={cardPremium}>
          <button
            type="button"
            onClick={dismissFloating}
            className={cn(
              'absolute right-1.5 top-1.5 rounded-md p-1 opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              premium ? 'text-white/80 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'
            )}
            aria-label={t('catalog.assistantDismiss')}
          >
            <X className="h-4 w-4" />
          </button>
          {inner}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        cardBase,
        'mb-5 p-4 shadow-sm sm:p-4',
        !premium && 'border-border/80 bg-card/90 backdrop-blur-[2px]'
      )}
      style={cardPremium}
    >
      {inner}
    </div>
  );
}
